// Superadmin console — tenants list with Seed Demo Data + delete actions.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api.js';

interface Tenant {
  id: string;
  slug: string;
  name: string;
  status: string;
  created_at: number;
  resident_count: number;
  election_count: number;
}

export function Super() {
  const qc = useQueryClient();
  const list = useQuery<{ tenants: Tenant[] }>({ queryKey: ['super-tenants'], queryFn: () => api.get('/super/tenants') });

  const [seedResult, setSeedResult] = useState<{ name: string; demoLoginUrl: string | null; counts: Record<string, number> } | null>(null);

  const seed = useMutation({
    mutationFn: () => api.post<{ tenant: { name: string }; counts: Record<string, number>; demoLoginUrl: string | null }>('/super/demo-seed'),
    onSuccess: (data) => {
      setSeedResult({ name: data.tenant.name, demoLoginUrl: data.demoLoginUrl, counts: data.counts });
      qc.invalidateQueries({ queryKey: ['super-tenants'] });
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/super/tenants/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['super-tenants'] }),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-ink-900">Operator console</h1>
          <p className="mt-1.5 text-ink-600">All HOAs on the platform.</p>
        </div>
        <button
          onClick={() => seed.mutate()}
          disabled={seed.isPending}
          className="inline-flex items-center gap-2 px-4 py-2 bg-ink-900 text-white text-sm font-medium rounded-lg hover:bg-ink-800 transition disabled:opacity-50"
        >
          {seed.isPending ? (
            <>
              <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Seeding…
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
              Seed demo HOA
            </>
          )}
        </button>
      </div>

      {seedResult && (
        <div className="mb-6 p-5 bg-brand-50 border border-brand-200 rounded-xl">
          <div className="font-semibold text-brand-900">Demo HOA created — {seedResult.name}</div>
          <div className="mt-1 text-sm text-brand-800">
            {seedResult.counts.lots} lots · {seedResult.counts.residents} residents · {seedResult.counts.elections} elections · {seedResult.counts.castBallots} ballots cast
          </div>
          {seedResult.demoLoginUrl && (
            <a
              href={seedResult.demoLoginUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:underline"
            >
              Sign in as demo board admin
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17l9.2-9.2M17 17V7H7" /></svg>
            </a>
          )}
        </div>
      )}

      <div className="bg-white border border-ink-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-50 border-b border-ink-200">
            <tr className="text-left">
              <th className="px-4 py-2.5 font-semibold text-ink-700">HOA</th>
              <th className="px-4 py-2.5 font-semibold text-ink-700">Slug</th>
              <th className="px-4 py-2.5 font-semibold text-ink-700">Residents</th>
              <th className="px-4 py-2.5 font-semibold text-ink-700">Elections</th>
              <th className="px-4 py-2.5 font-semibold text-ink-700">Created</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={6} className="px-4 py-8 text-center text-ink-500">Loading…</td></tr>}
            {list.data?.tenants.map((t) => (
              <tr key={t.id} className="border-b border-ink-100 last:border-b-0 hover:bg-ink-50/50 transition">
                <td className="px-4 py-2.5 font-medium text-ink-900">{t.name}</td>
                <td className="px-4 py-2.5"><code className="font-mono text-xs text-ink-600">{t.slug}</code></td>
                <td className="px-4 py-2.5 text-ink-700">{t.resident_count}</td>
                <td className="px-4 py-2.5 text-ink-700">{t.election_count}</td>
                <td className="px-4 py-2.5 text-ink-600 text-xs">{new Date(t.created_at).toLocaleString()}</td>
                <td className="px-4 py-2.5 text-right">
                  <button
                    onClick={() => {
                      if (confirm(`Delete HOA "${t.name}" and ALL its data? This cannot be undone.`)) {
                        del.mutate(t.id);
                      }
                    }}
                    disabled={del.isPending}
                    className="text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {list.data?.tenants.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-ink-500">No HOAs yet. Seed a demo or wait for someone to sign up.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
